import { useId } from 'react';
import type { ProjectGraphEdge } from '@avisoma/shared';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/16/solid';
import { Badge } from '../../catalyst/badge';
import { Link } from '../../components/Link';
import { linkedPageIds, readingsSummary, roleExplanation } from './mentionEvidence';
import { NodeButton } from './NodeButton';

type Props = {
  edge: ProjectGraphEdge;
  /** The name of the node at the other end of the edge. */
  label: string;
  /** The label of the filter that hides the other node (`Parts`, `LLP Status`), or `null` when none does. */
  hiddenFilter: string | null;
  /** Brings the other node into the view and selects it. */
  onSelect: () => void;
  /** Clears `hiddenFilter`, then does what `onSelect` does. */
  onShowAndReveal: () => void;
  /** The evidence shows. Held by the list, so a row scrolled out of a windowed list and back keeps it. */
  open: boolean;
  onToggle: () => void;
};

const pages = (count: number) => `${count} ${count === 1 ? 'page' : 'pages'}`;

/**
 * One relationship of the selected node: the other node's name, and a page count that opens the evidence,
 * the role in one line, what the document's rows state about the entity, and the linked source pages. A node a filter hides says which filter, with a button
 * that clears it.
 */
export function MentionRow({ edge, label, hiddenFilter, onSelect, onShowAndReveal, open, onToggle }: Props) {
  const detailsId = useId();
  const hiddenNoteId = useId();
  const pageIds = linkedPageIds(edge);
  const readings = readingsSummary(edge);
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <NodeButton
          label={label}
          muted={hiddenFilter !== null}
          describedBy={hiddenFilter !== null ? hiddenNoteId : undefined}
          onClick={onSelect}
        />
        {edge.role === 'subject' && <Badge color="indigo">subject</Badge>}
        <button
          type="button"
          aria-expanded={open}
          aria-controls={open ? detailsId : undefined}
          className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs text-zinc-600 ring-1 ring-zinc-950/10 hover:bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-zinc-300 dark:ring-white/15 dark:hover:bg-zinc-700"
          onClick={onToggle}
        >
          <Chevron className="size-3.5 shrink-0 text-zinc-500 dark:text-zinc-400" aria-hidden />
          {pages(pageIds.length)}
        </button>
      </div>
      {hiddenFilter !== null && (
        <div className="flex flex-wrap items-center gap-x-2 text-xs text-zinc-500 dark:text-zinc-400">
          <span id={hiddenNoteId}>Hidden by the {hiddenFilter} filter</span>
          <button
            type="button"
            aria-label={`Show and reveal ${label}`}
            className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
            onClick={onShowAndReveal}
          >
            Show and reveal
          </button>
        </div>
      )}
      {open && (
        <div
          id={detailsId}
          className="ml-1 flex flex-col gap-1 border-l-2 border-zinc-200 pl-3 text-xs dark:border-zinc-700"
        >
          <p className="text-zinc-700 dark:text-zinc-300">{roleExplanation(edge.role)}</p>
          {readings !== undefined && <p className="text-zinc-500 dark:text-zinc-400">{readings}</p>}
          {pageIds.length === 0 ? (
            <p className="text-zinc-500 dark:text-zinc-400">No linked page was recorded for this mention.</p>
          ) : (
            <>
              <p className="font-medium text-zinc-700 dark:text-zinc-300">Linked source pages</p>
              <div className="flex flex-wrap gap-x-2 gap-y-1">
                {pageIds.map((pageId) => (
                  <Link
                    key={pageId}
                    to={`/document-pages/${encodeURIComponent(pageId)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    page {pageId.slice(0, 8)}
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
